import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
Check,
Copy,
Clock,
ShieldAlert,
MessageCircle,
CreditCard,
LogOut,
Loader2,
Sparkles,
} from "lucide-react";
import Logo from "@/components/Logo";
import { toast } from "sonner";
import { brl } from "@/lib/format";
import { initMercadoPago, CardPayment } from "@mercadopago/sdk-react";
const MERCADOPAGO_PUBLIC_KEY = process.env.REACT_APP_MERCADOPAGO_PUBLIC_KEY;

if (MERCADOPAGO_PUBLIC_KEY) {
initMercadoPago(MERCADOPAGO_PUBLIC_KEY);
}
const STATE_META = {
trial: {
label: "Teste gratuito",
tone: "amber",
icon: Clock,
banner: "Você está no período de teste gratuito.",
},
active: {
label: "Plano ativo",
tone: "emerald",
icon: Check,
banner: "Seu plano está ativo.",
},
expired: {
label: "Expirado",
tone: "rose",
icon: ShieldAlert,
banner:
"Seu período gratuito de 3 dias terminou. Escolha um plano para continuar utilizando o serviço.",
},
pending_trial: {
label: "Aguardando ativação",
tone: "amber",
icon: Clock,
banner: "Seu trial ainda não iniciou.",
},
pending: {
label: "Aguardando aprovação",
tone: "amber",
icon: Clock,
banner:
"Sua conta está aguardando aprovação do administrador. Assim que aprovado, seu teste gratuito de 3 dias começa.",
},
rejected: {
label: "Perfil bloqueado",
tone: "rose",
icon: ShieldAlert,
banner:
"Sua conta está bloqueada. Escolha um plano para reativar ou fale com o administrador.",
},
};

const TONE_MAP = {
amber: "border-amber-500/40 bg-amber-500/10 text-amber-200",
emerald: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200",
rose: "border-rose-500/40 bg-rose-500/10 text-rose-200",
};

export default function Plans() {
const { user, logout } = useAuth();
const navigate = useNavigate();
const [status, setStatus] = useState(null);
const [loading, setLoading] = useState(true);
const [selectedPlan, setSelectedPlan] = useState(null);
const [paymentLoading, setPaymentLoading] = useState(false);
const [paymentReceipt, setPaymentReceipt] = useState(null);

const load = async () => {
setLoading(true);
try {
const { data } = await api.get("/billing/status");
setStatus(data);
} catch (e) {
toast.error(formatApiError(e));
} finally {
setLoading(false);
}
};

useEffect(() => {
load();
}, []);

const copyPix = async () => {
try {
await navigator.clipboard.writeText(status?.pix_key || "");
toast.success("Chave PIX copiada");
} catch {
toast.error("Não foi possível copiar");
}
};

const openWhatsApp = (plan) => {
const phone = (status?.pix_key || "").replace(/\D/g, "");

if (!phone) {
toast.error("Chave PIX indisponível.");
return;
}

const msg = `Olá! Sou ${user?.name || user?.email} e acabei de pagar o plano *${plan.label} (${brl(plan.price)})* via PIX. Por favor, ativar meu acesso. Meu email: ${user?.email}`;

window.open(
`https://wa.me/55${phone}?text=${encodeURIComponent(msg)}`,
"_blank",
"noopener"
);
};

const startCardPayment = (plan) => {
setSelectedPlan(plan);
setPaymentLoading(false);
};
const handleCardSubmit = async (formData, additionalData) => {
if (!selectedPlan) {
toast.error("Selecione um plano primeiro.");
return;
}

setPaymentLoading(true);

try {
const paymentTypeId = additionalData?.paymentTypeId;

if (!paymentTypeId) {
throw new Error("Não foi possível identificar o tipo do cartão.");
}

const { data } = await api.post("/billing/mercadopago/card", {
plan_type: selectedPlan.key,
token: formData.token,
payment_method_id: formData.payment_method_id,
payment_type_id: paymentTypeId,
installments: Number(formData.installments),
payer: formData.payer,
});

if (data?.status === "approved") {
const receipt = {
orderId: data.order_id,
plan: selectedPlan.label,
planType: selectedPlan.key,
amount: Number(selectedPlan.price),
customerName: user?.name || user?.email || "Cliente",
customerEmail: user?.email || "",
paidAt: new Date().toISOString(),
expiresAt: data.expires_at,
};

setPaymentReceipt(receipt);
setSelectedPlan(null);

toast.success("Pagamento aprovado! Seu plano foi ativado.");

await load();
return;
}

if (data?.status === "processing") {
toast.info("Pagamento em processamento. Aguarde a confirmação.");
return;
}

toast.error(
data?.message || "O pagamento não foi aprovado."
);
} catch (e) {
toast.error(formatApiError(e));
throw e;
} finally {
setPaymentLoading(false);
}
};

const state = status?.state || user?.plan_state || "expired";

const meta = STATE_META[state] || STATE_META.expired;
const Icon = meta.icon;

return (
<div
  className="min-h-screen bg-[#060A12] text-slate-100 overflow-x-hidden"
  data-testid="plans-page"
>
<header
  className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#060A12]/95 border-b border-slate-800/80"
  style={{ paddingTop: "env(safe-area-inset-top)" }}
>
<div className="max-w-6xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between gap-2">
<div className="flex items-center gap-3 min-w-0">
<Logo size={34} />
<div className="min-w-0">
<div className="text-white font-bold tracking-tight text-sm sm:text-base" style={{ fontFamily: "Outfit" }}>
{user?.business_name || "MS Soluções Financeiras"}
</div>
<div className="text-[10px] uppercase tracking-widest text-slate-400 hidden sm:block">
Assinatura
</div>
</div>
</div>
<Button
variant="ghost"
size="sm"
className="text-slate-300 hover:bg-slate-800"
onClick={async () => {
await logout();
navigate("/login", { replace: true });
}}
data-testid="plans-logout-btn"
>
<LogOut className="w-4 h-4 mr-2" /> Sair
</Button>
</div>
</header>

<main className="max-w-5xl mx-auto px-3 sm:px-4 md:px-8 pt-24 sm:pt-28 pb-6 sm:pb-8 space-y-5 sm:space-y-6">
<div>
<div className="text-[10px] sm:text-xs uppercase tracking-widest text-blue-400 font-semibold mb-2">
Assinatura
</div>
<h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" style={{ fontFamily: "Outfit" }}>
Escolha seu plano
</h1>
<p className="text-slate-400 mt-1 text-sm">
Continue com o painel completo de gestão de empréstimos. Ativação é feita manualmente pelo administrador após o pagamento via PIX.
</p>
</div>

<div
className={`glass-card p-4 flex items-start gap-3 border ${TONE_MAP[meta.tone]}`}
data-testid="plans-status-banner"
>
<Icon className="w-5 h-5 shrink-0 mt-0.5" />
<div className="flex-1 text-sm">
<div className="font-semibold">{meta.label}</div>
<div className="opacity-90">{meta.banner}</div>
{state === "trial" && status?.days_remaining != null && (
<div className="text-xs mt-1 opacity-80" data-testid="trial-remaining">
Restam {status.days_remaining} {status.days_remaining === 1 ? "dia" : "dias"} do seu teste gratuito.
</div>
)}
{state === "active" && status?.expires_at && (
<div className="text-xs mt-1 opacity-80">
Plano <strong>{status.plan_type}</strong>. Válido até{" "}
{new Date(status.expires_at).toLocaleDateString("pt-BR")}.
</div>
)}
</div>
{state === "trial" || state === "active" ? (
<Button
size="sm"
variant="outline"
className="border-slate-700 text-slate-200 hover:bg-slate-800"
onClick={() => navigate("/")}
data-testid="plans-back-btn"
>
Voltar ao painel
</Button>
) : null}
</div>

{loading ? (
<div className="py-16 text-center text-slate-500 flex items-center justify-center gap-2">
<Loader2 className="w-4 h-4 animate-spin" /> Carregando planos...
</div>
) : (
<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
{(status?.plans || []).map((plan, idx) => {
const isFeatured = plan.key === "semestral";
return (
<div
key={plan.key}
className={`relative rounded-2xl border p-4 sm:p-5 lg:p-6 flex flex-col gap-3 sm:gap-4 min-w-0 transition-all ${
                  isFeatured
                    ? "border-blue-500/60 bg-gradient-to-br from-blue-500/15 to-slate-900/60 shadow-[0_0_40px_-15px_rgba(59,130,246,0.5)]"
                    : "border-slate-800 bg-slate-900/40 hover:border-slate-700"
                }`}
data-testid={`plan-card-${plan.key}`}
>
{isFeatured && (
<Badge className="absolute -top-3 left-6 bg-blue-500 text-white border-0">
<Sparkles className="w-3 h-3 mr-1" /> Mais escolhido
</Badge>
)}
<div>
<div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
Plano
</div>
<div className="text-2xl font-bold text-white mt-1" style={{ fontFamily: "Outfit" }}>
{plan.label}
</div>
</div>
<div>
<div className="text-3xl sm:text-4xl font-bold text-white font-mono break-words">{brl(plan.price)}</div>
<div className="text-xs text-slate-500 mt-1">
Acesso completo por {plan.duration_days} dias
</div>
</div>
<ul className="text-xs sm:text-sm text-slate-300 space-y-2 flex-1">
<li className="flex items-start gap-2">
<Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
Clientes ilimitados
</li>
<li className="flex items-start gap-2">
<Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
Regra de crédito rotativo + multa de atraso
</li>
<li className="flex items-start gap-2">
<Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
Simulador de propostas + recibos por link
</li>
<li className="flex items-start gap-2">
<Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
Painel PWA (instalável no celular)
</li>
</ul>
<Button
  className={`w-full ${
    isFeatured
      ? "bg-blue-600 hover:bg-blue-500 text-white"
      : "bg-slate-800 hover:bg-slate-700 text-slate-100"
  }`}
  onClick={() => startCardPayment(plan)}
  data-testid={`plan-select-${plan.key}`}
>
  <CreditCard className="w-4 h-4 mr-2" /> Pagar com cartão
</Button>
</div>
);
})}
</div>
)}

{selectedPlan && (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
    role="dialog"
    aria-modal="true"
    aria-labelledby="card-payment-title"
  >
    <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-slate-700 bg-[#0B1120] shadow-2xl">

      <div className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-slate-800 bg-[#0B1120]/95 backdrop-blur px-5 py-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-blue-400 font-semibold">
            Pagamento com cartão
          </div>

          <div
            id="card-payment-title"
            className="text-xl font-bold text-white mt-1"
            style={{ fontFamily: "Outfit" }}
          >
            {selectedPlan.label}
          </div>

          <div className="text-sm text-slate-400 mt-1">
            Total: {brl(selectedPlan.price)}
          </div>
        </div>

        <Button
          type="button"
          variant="ghost"
          className="text-slate-300 hover:bg-slate-800"
          onClick={() => !paymentLoading && setSelectedPlan(null)}
          disabled={paymentLoading}
          aria-label="Fechar pagamento"
        >
          ✕
        </Button>
      </div>

      <div className="p-5">
        <CardPayment
          initialization={{
            amount: Number(selectedPlan.price),
            payer: {
              email: user?.email || "",
            },
          }}
          onSubmit={handleCardSubmit}
          onError={(error) => {
            console.error("Mercado Pago CardPayment:", error);
            toast.error("Não foi possível carregar o pagamento.");
            setPaymentLoading(false);
          }}
        />

        {paymentLoading && (
          <div className="mt-4 flex items-center justify-center gap-2 text-sm text-slate-400">
            <Loader2 className="w-4 h-4 animate-spin" />
            Processando pagamento...
          </div>
        )}
      </div>
    </div>
  </div>
)}

{paymentReceipt && (
  <div
    className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
    role="dialog"
    aria-modal="true"
    aria-labelledby="receipt-title"
  >
    <div
      id="payment-receipt"
      className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border border-emerald-500/30 bg-[#0B1120] shadow-2xl"
    >
      <div className="p-6">

        <div className="text-center border-b border-slate-800 pb-5">
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-semibold">
            Pagamento aprovado
          </div>

          <h2
            id="receipt-title"
            className="text-2xl font-bold text-white mt-2"
            style={{ fontFamily: "Outfit" }}
          >
            Comprovante de pagamento
          </h2>

          <p className="text-sm text-slate-400 mt-2">
            MSSYSTEM
          </p>
        </div>

        <div className="mt-5 space-y-4 text-sm">

          <div className="flex justify-between gap-4">
            <span className="text-slate-400">Cliente</span>
            <span className="text-white text-right">
              {paymentReceipt.customerName}
            </span>
          </div>

          <div className="flex justify-between gap-4">
            <span className="text-slate-400">E-mail</span>
            <span className="text-white text-right break-all">
              {paymentReceipt.customerEmail}
            </span>
          </div>

          <div className="flex justify-between gap-4">
            <span className="text-slate-400">Plano</span>
            <span className="text-white text-right">
              {paymentReceipt.plan}
            </span>
          </div>

          <div className="flex justify-between gap-4">
            <span className="text-slate-400">Valor pago</span>
            <span className="text-white font-semibold">
              {brl(paymentReceipt.amount)}
            </span>
          </div>

          <div className="flex justify-between gap-4">
            <span className="text-slate-400">Data do pagamento</span>
            <span className="text-white text-right">
              {new Date(paymentReceipt.paidAt).toLocaleString("pt-BR")}
            </span>
          </div>

          <div className="flex justify-between gap-4">
            <span className="text-slate-400">Plano válido até</span>
            <span className="text-white text-right">
              {paymentReceipt.expiresAt
                ? new Date(paymentReceipt.expiresAt).toLocaleDateString("pt-BR")
                : "-"}
            </span>
          </div>

          <div className="pt-4 border-t border-slate-800">
            <div className="flex justify-between gap-4">
              <span className="text-slate-400">
                ID da transação
              </span>

              <span className="text-white text-right font-mono text-xs break-all">
                {paymentReceipt.orderId || "-"}
              </span>
            </div>
          </div>

        </div>

        <div className="mt-5 rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-3 text-xs text-emerald-200">
          Pagamento confirmado pelo Mercado Pago e plano ativado no MSSYSTEM.
        </div>

        <div className="mt-5 flex flex-col sm:flex-row gap-2">

          <Button
            className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white"
            onClick={() => window.print()}
          >
            Imprimir / Salvar PDF
          </Button>

          <Button
            variant="outline"
            className="flex-1 border-slate-700 text-slate-200 hover:bg-slate-800"
            onClick={() => {
              setPaymentReceipt(null);
              navigate("/");
            }}
          >
            Continuar
          </Button>

        </div>

        <div className="mt-4 text-center text-[11px] text-slate-500">
          Comprovante gerado pelo MSSYSTEM com base na confirmação do pagamento.
        </div>

      </div>
    </div>
  </div>
)}

<div className="glass-card p-5" data-testid="plans-pix-section">
  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">

    <div>
      <div className="text-[10px] uppercase tracking-widest text-blue-400 font-semibold">
        Pagamento por PIX
      </div>

      <div
        className="text-xl font-bold text-white mt-1"
        style={{ fontFamily: "Outfit" }}
      >
        Chave PIX (telefone)
      </div>

      <div className="text-sm text-slate-400 mt-1">
        1. Pague pelo PIX. 2. Envie o comprovante via WhatsApp. 3. Aguarde a ativação manual pelo administrador.
      </div>
    </div>

    <div className="flex flex-col sm:flex-row items-stretch gap-2">

      <div className="min-w-0 break-all rounded-xl bg-slate-900/60 border border-slate-700 px-3 sm:px-4 py-3 font-mono text-sm sm:text-lg text-slate-100 tracking-wide">
        {status?.pix_key || "-"}
      </div>

      <Button
        variant="outline"
        className="border-slate-700 text-slate-200 hover:bg-slate-800"
        onClick={copyPix}
        data-testid="plans-copy-pix"
      >
        <Copy className="w-4 h-4 mr-2" /> Copiar chave
      </Button>

    </div>

  </div>
</div>

</main>
</div>
);
}
